import React, { useState, useEffect } from 'react'
import { formatDate } from '../components/utils/dateUtils'
import Modal from 'react-modal'
import CancelPlotSlip from './CancelPlotSlip'
import { Printer, X } from 'lucide-react'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import { getCancelPlotDetails } from '../api'

const PremiumCanceledPlotsList = ({
  canceledPlots,
  onSelectPlot,
  selectedId,
  themeColor = '#0d9488',
  users = []
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredPlots, setFilteredPlots] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hoveredItemId, setHoveredItemId] = useState(null)
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [selectedPlotForPrint, setSelectedPlotForPrint] = useState(null)
  const [loadingPrintData, setLoadingPrintData] = useState(false)
  const { activeSociety } = useSidebarContext()
  // Format number to PKR currency format
  const formatCurrency = amount => {
    return `PKR ${amount.toLocaleString('en-PK')}`
  }

  // Completely rewritten findCustomerByPlot function with aggressive fallback
  const findCustomerByPlot = plot => {

    // First, check if we have customer_details from the backend transformation
    if (plot.customer_details) {
      return {
        name: plot.customer_details.name,
        phone: plot.customer_details.phone || 'N/A',
        email: plot.customer_details.email || 'RAZA',
        profilePic: plot.customer_details.profilePic || null
      }
    }

// Second, check direct customer_id population (only if it's an object, not a string)
if (plot.customer_id && typeof plot.customer_id === 'object') {
  const customer = plot.customer_id

  return {
    name: customer.name ||
          customer.customerData?.name ||
          `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
          customer.username ||
          'Unknown',
    phone: customer.contactInfo?.phone || 
           customer.customerData?.contactInfo?.phone || 
           customer.phone || 
           'N/A',
    email: customer.contactInfo?.email || 
           customer.customerData?.contactInfo?.email || 
           customer.email || 
           'N/A',
    profilePic: customer.profile_picture || null
  }
}

    // Third, if customer_id is just a string ID, search in users array
    if (
      plot.customer_id &&
      typeof plot.customer_id === 'string' &&
      Array.isArray(users) &&
      users.length > 0
    ) {
      const customer = users.find(user => user._id === plot.customer_id)

      if (customer) {
        return {
          name:
            customer.customerData?.name ||
            `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
            customer.username ||
            'N/RAZAK',
          phone:
            customer.customerData?.contactInfo?.phone ||
            customer.phone ||
            'N/A',
          email:
            customer.customerData?.contactInfo?.email ||
            customer.email ||
            'N/A',
          profilePic: customer.profile_picture || null
        }
      }
    }

// Fourth, check original_booking.customer_id (if it's an object)
if (
  plot.original_booking?.customer_id &&
  typeof plot.original_booking.customer_id === 'object'
) {
  const customer = plot.original_booking.customer_id

  return {
    name: customer.name || 
          `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
          customer.username ||
          'Unknown',
    phone: customer.contactInfo?.phone || customer.phone || 'N/A',
    email: customer.contactInfo?.email || customer.email || 'N/A',
    profilePic: customer.profile_picture || null
  }
}

    // Fifth, check original_booking.customer_id if it's a string
    if (
      plot.original_booking?.customer_id &&
      typeof plot.original_booking.customer_id === 'string' &&
      Array.isArray(users) &&
      users.length > 0
    ) {
      const customer = users.find(
        user => user._id === plot.original_booking.customer_id
      )

      if (customer) {
        return {
          name:
            customer.customerData?.name ||
            `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
            customer.username ||
            'N/SAEED',
          phone:
            customer.customerData?.contactInfo?.phone ||
            customer.phone ||
            'N/A',
          email:
            customer.customerData?.contactInfo?.email ||
            customer.email ||
            'N/A',
          profilePic: customer.profile_picture || null
        }
      }
    }

    // Sixth, fallback to searching in users array by matching plot_id (if available)
    if (plot?.plot_id?._id && Array.isArray(users) && users.length > 0) {
      for (const user of users) {
        if (user.role === 'Customer' && user.customerData?.plots) {
          for (const userPlot of user.customerData.plots) {
            if (
              userPlot._id === plot.plot_id._id ||
              userPlot === plot.plot_id._id
            ) {
              // console.log('Match found in users array by plot!')
              return {
                name: user.customerData.name || user.username || 'N/ANTI',
                phone: user.customerData.contactInfo?.phone || 'N/A',
                email: user.customerData.contactInfo?.email || 'N/A',
                profilePic: user.profile_picture || null
              }
            }
          }
        }
      }
    }

    // Final fallback
    return {
      name: 'Unknown Customer',
      phone: 'N/A',
      email: 'N/A',
      profilePic: null
    }
  }

  // Filter plots based on search term and remove duplicates
  useEffect(() => {
    if (!Array.isArray(canceledPlots)) {
      setFilteredPlots([])
      return
    }

    setIsLoading(true)

    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      // Remove duplicates by _id first
      const uniquePlotsMap = new Map()
      canceledPlots.forEach(plot => {
        if (plot && plot._id) {
          uniquePlotsMap.set(plot._id.toString(), plot)
        }
      })
      const uniquePlots = Array.from(uniquePlotsMap.values())

      // Then filter by search term
      const filtered = uniquePlots.filter(plot => {
        // Get customer details for search
        const customer = findCustomerByPlot(plot)
        const customerName = customer?.name || ''
        const plotNumber = plot.plot_id?.plot_number || ''

        // Search in customer name or plot number
        const searchLower = searchTerm.toLowerCase()
        return (
          customerName.toLowerCase().includes(searchLower) ||
          plotNumber.toLowerCase().includes(searchLower)
        )
      })

      setFilteredPlots(filtered)
      setIsLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm, canceledPlots, users])

  // Helper function to get initials from name
  const getInitials = name => {
    if (!name || name === 'N/A') return '?'
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  // Check if we have any plots
  const hasPlots = Array.isArray(canceledPlots) && canceledPlots.length > 0

  if (!hasPlots) {
    return (
      <div className='bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-xl shadow-xl p-10 flex flex-col items-center justify-center h-full backdrop-blur-sm backdrop-filter bg-opacity-80 border border-slate-700 border-opacity-40'>
        <div className='p-4 rounded-full bg-slate-700 bg-opacity-50 mb-4'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-12 w-12 text-teal-300 opacity-70'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={1.5}
              d='M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <p className='text-teal-300 text-center font-medium'>
          No canceled plots found for this society
        </p>
        <p className='text-teal-300 text-center opacity-70 mt-2 text-sm'>
          Canceled plots will appear here once they are registered
        </p>
      </div>
    )
  }

  return (
    <div className='bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-xl shadow-xl overflow-hidden backdrop-blur-sm backdrop-filter bg-opacity-80 border border-slate-700 border-opacity-40'>
      {/* Header with search */}
      <div className='p-5 border-b border-slate-700 border-opacity-50'>
        <h2 className='text-xl font-medium text-teal-300 mb-4'>
          Canceled Plots
        </h2>

        {/* Search bar */}
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-5 w-5 text-teal-300 opacity-70'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
          </div>
          <input
            type='search'
            className='bg-slate-700 bg-opacity-40 text-teal-300 placeholder-teal-300 placeholder-opacity-50 block w-full pl-10 pr-3 py-2 rounded-lg border border-slate-600 focus:ring-2 focus:ring-teal-300 focus:ring-opacity-50 focus:border-teal-300 focus:outline-none transition-all duration-300'
            placeholder='Search by plot number or customer name...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Stats */}
        <div className='flex items-center justify-between mt-4'>
          <div className='flex items-center'>
            <div className='text-teal-300 flex items-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5 mr-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
              <span className='font-medium'>{filteredPlots.length}</span>
              <span className='ml-1 text-sm opacity-70'>
                of {canceledPlots.length}
              </span>
            </div>
          </div>

          <div className='flex space-x-2'>
            <div className='px-3 py-1 rounded-lg bg-teal-900 bg-opacity-30 text-teal-300 text-xs flex items-center border border-teal-400 border-opacity-20'>
              <div className='h-2 w-2 rounded-full bg-teal-300 mr-2'></div>
              <span>
                {canceledPlots.filter(plot => plot.refund_completed).length}{' '}
                Completed
              </span>
            </div>
            <div className='px-3 py-1 rounded-lg bg-amber-900 bg-opacity-30 text-amber-300 text-xs flex items-center border border-amber-400 border-opacity-20'>
              <div className='h-2 w-2 rounded-full bg-amber-300 mr-2'></div>
              <span>
                {canceledPlots.filter(plot => !plot.refund_completed).length}{' '}
                Pending
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className='flex justify-center items-center py-8'>
          <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-300'></div>
        </div>
      )}

      {/* Empty search results */}
      {!isLoading && filteredPlots.length === 0 && searchTerm && (
        <div className='flex flex-col items-center justify-center py-12'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-16 w-16 text-teal-300 opacity-50 mb-4'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={1.5}
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
          <p className='text-teal-300 text-lg font-medium'>No results found</p>
          <p className='text-teal-300 opacity-70 mt-1'>
            Try searching with a different term
          </p>
        </div>
      )}

      {/* Plot list */}
      {!isLoading && filteredPlots.length > 0 && (
        <div className='overflow-y-auto max-h-[800px] scrollbar-thin scrollbar-thumb-teal-300 scrollbar-track-slate-700'>
          <ul className='divide-y divide-slate-700 divide-opacity-50'>
            {filteredPlots.map((plot, index) => {
              // Additional duplicate check for safety
              const isDuplicate = filteredPlots.findIndex(
                p => p._id && p._id.toString() === plot._id?.toString()
              ) !== index
              
              if (isDuplicate) return null
              
              const customerDetails = findCustomerByPlot(plot)
              const refundAmount = plot.refunded_amount || 0
              const totalRefundAmount =
                plot.total_refund_amount || plot.remaining_balance || 0
              const refundPercentage =
                totalRefundAmount > 0
                  ? (refundAmount / totalRefundAmount) * 100
                  : 0

              const isSelected = selectedId === plot._id
              const isHovered = hoveredItemId === plot._id

              return (
                <li
                  key={plot._id}
                  onClick={() => onSelectPlot(plot._id)}
                  onMouseEnter={() => setHoveredItemId(plot._id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  className={`p-5 cursor-pointer transition-all duration-300 ease-in-out
                    ${isSelected ? 'border-l-4' : ''}
                  `}
                  style={{
                    borderLeftColor: isSelected ? themeColor : 'transparent',
                    backgroundColor: isSelected
                      ? `rgba(13, 148, 136, 0.1)`
                      : isHovered
                      ? `rgba(13, 148, 136, 0.05)`
                      : 'transparent'
                  }}
                >
                  <div className='flex items-start gap-4'>
                    {/* Avatar/Initials */}
                    <div className='flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-slate-700 bg-opacity-50 border border-slate-600 shadow-lg text-teal-300 font-bold text-lg'>
                      {customerDetails?.profilePic ? (
                        <img
                          src={customerDetails.profilePic}
                          alt={customerDetails.name}
                          className='w-12 h-12 rounded-full object-cover'
                        />
                      ) : (
                        getInitials(customerDetails?.name || 'Joshua')
                      )}
                    </div>

                    <div className='flex-grow'>
                      {/* Main info */}
                      <div className='flex justify-between'>
                        <div>
                          <div className='flex items-center'>
                            <p className='font-medium text-teal-300 text-lg'>
                              Plot {plot.plot_id?.plot_number}
                            </p>
                            {plot.plot_id?.block && (
                              <span className='ml-2 px-2 py-0.5 bg-slate-700 bg-opacity-50 text-teal-300 rounded-md text-xs'>
                                Block {plot.plot_id.block}
                              </span>
                            )}
                          </div>
                          <p className='text-teal-300 opacity-90 mt-1'>
                            {customerDetails?.name || 'Unknown Customer'}
                          </p>
                          <p className='text-teal-300 opacity-70 text-sm mt-0.5'>
                            {customerDetails?.phone} • {customerDetails?.email}
                          </p>
                        </div>

                        <div className='text-right'>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                              plot.refund_completed
                                ? 'bg-teal-900 bg-opacity-30 text-teal-300 border border-teal-400 border-opacity-20'
                                : 'bg-amber-900 bg-opacity-30 text-amber-300 border border-amber-400 border-opacity-20'
                            }`}
                          >
                            {plot.refund_completed ? (
                              <>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  className='h-3 w-3 mr-1'
                                  fill='none'
                                  viewBox='0 0 24 24'
                                  stroke='currentColor'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M5 13l4 4L19 7'
                                  />
                                </svg>
                                Completed
                              </>
                            ) : (
                              <>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  className='h-3 w-3 mr-1'
                                  fill='none'
                                  viewBox='0 0 24 24'
                                  stroke='currentColor'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                                  />
                                </svg>
                                In Progress
                              </>
                            )}
                          </span>
                          <p className='text-teal-300 opacity-70 text-base mt-2'>
                            Created {formatDate(plot.createdAt)}
                          </p>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              setLoadingPrintData(true)
                              try {
                                // Fetch full details with society populated
                                const fullDetails = await getCancelPlotDetails(plot._id)
                                setSelectedPlotForPrint(fullDetails)
                                setPrintModalOpen(true)
                              } catch (error) {
                                console.error('Error fetching plot details for print:', error)
                                // Fallback to using plot data from list
                                setSelectedPlotForPrint(plot)
                                setPrintModalOpen(true)
                              } finally {
                                setLoadingPrintData(false)
                              }
                            }}
                            className='mt-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs flex items-center gap-1.5 transition-all duration-300 shadow-md hover:shadow-lg'
                            title='Print Cancellation Slip'
                            disabled={loadingPrintData}
                          >
                            <Printer className='w-3 h-3' />
                            {loadingPrintData ? 'Loading...' : 'Print Slip'}
                          </button>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className='mt-4'>
                        <div className='w-full bg-slate-700 bg-opacity-50 rounded-full h-2.5 overflow-hidden'>
                          <div
                            className='h-2.5 rounded-full transition-all duration-500 relative'
                            style={{
                              width: `${refundPercentage}%`,
                              background: `linear-gradient(to right, rgba(45, 212, 191, 0.5), rgba(45, 212, 191, 0.9))`
                            }}
                          >
                            <div className='absolute top-0 h-full w-full bg-teal-300 bg-opacity-30 animate-pulse'></div>
                          </div>
                        </div>
                        <div className='flex justify-between text-xs text-teal-300 mt-2'>
                          <span>{formatCurrency(refundAmount)}</span>
                          <span className='font-medium'>
                            {refundPercentage.toFixed(0)}%
                          </span>
                          <span>{formatCurrency(totalRefundAmount)}</span>
                        </div>
                      </div>

                      {/* Additional Details */}
                      <div className='mt-3 flex flex-wrap gap-2'>
                        {plot.refund_type && (
                          <span className='px-2 py-0.5 bg-slate-700 bg-opacity-40 text-teal-300 opacity-80 rounded text-xs'>
                            Type: {plot.refund_type}
                          </span>
                        )}
                        {plot.plot_id?.size && (
                          <span className='px-2 py-0.5 bg-slate-700 bg-opacity-40 text-teal-300 opacity-80 rounded text-xs'>
                            Size: {plot.plot_id.size}
                          </span>
                        )}
                        {plot.dealer_commission_deducted > 0 && (
                          <span className='px-2 py-0.5 bg-slate-700 bg-opacity-40 text-teal-300 opacity-80 rounded text-xs'>
                            Commission:{' '}
                            {formatCurrency(plot.dealer_commission_deducted)}
                          </span>
                        )}
                        {plot.society_deduction_percentage > 0 && (
                          <span className='px-2 py-0.5 bg-slate-700 bg-opacity-40 text-teal-300 opacity-80 rounded text-xs'>
                            Deduction: {plot.society_deduction_percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Print Modal */}
      <Modal
        isOpen={printModalOpen}
        onRequestClose={() => {
          setPrintModalOpen(false)
          setSelectedPlotForPrint(null)
        }}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '20px'
          }
        }}
      >
        <div className='relative'>
          <button
            onClick={() => {
              setPrintModalOpen(false)
              setSelectedPlotForPrint(null)
            }}
            className='absolute right-0 top-0 p-2 hover:bg-slate-700 rounded-full text-teal-300 transition-colors'
          >
            <X className='w-5 h-5' />
          </button>
          {selectedPlotForPrint && (
            <CancelPlotSlip
              cancelPlot={selectedPlotForPrint}
              companyInfo={activeSociety}
            />
          )}
        </div>
      </Modal>
    </div>
  )
}

export default PremiumCanceledPlotsList
